import React from 'react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';

interface ApiErrorFallbackProps {
  error: any;
  onRetry?: () => void;
  title?: string;
  description?: string;
  showDetails?: boolean;
}

export function ApiErrorFallback({ 
  error, 
  onRetry, 
  title = "Unable to load data",
  description,
  showDetails = false 
}: ApiErrorFallbackProps) {
  // Check error types
  const is401 = error?.status === 401;
  const is403 = error?.status === 403;
  const is404 = error?.status === 404;
  const is500 = error?.status >= 500;
  const isNetworkError = !error?.status && error?.message?.includes('fetch');

  let errorTitle = title;
  let errorDescription = description;
  let actionButton = null;

  if (is401) {
    errorTitle = "Authentication required";
    errorDescription = "Your session has expired. Please log in again to continue.";
    actionButton = (
      <Button onClick={() => window.location.href = '/login'} className="mt-4">
        Go to Login
      </Button>
    );
  } else if (is403) {
    errorTitle = "Access denied";
    errorDescription = "You don't have permission to access this resource. Please contact your administrator.";
  } else if (is404) {
    errorTitle = "Service not available";
    errorDescription = "This feature is currently being deployed. Please try again in a few minutes.";
    actionButton = (
      <Button onClick={onRetry} variant="outline" className="mt-4">
        Try Again
      </Button>
    );
  } else if (is500) {
    errorTitle = "Server error";
    errorDescription = "The server encountered an error. Our team has been notified. Please try again later.";
    actionButton = (
      <Button onClick={onRetry} variant="outline" className="mt-4">
        Try Again
      </Button>
    );
  } else if (isNetworkError) {
    errorTitle = "Connection error";
    errorDescription = "Unable to connect to the server. Please check your internet connection.";
    actionButton = (
      <Button onClick={onRetry} variant="outline" className="mt-4">
        Retry Connection
      </Button>
    );
  } else {
    errorDescription = description || error?.message || "An unexpected error occurred while loading the data.";
    actionButton = onRetry && (
      <Button onClick={onRetry} variant="outline" className="mt-4">
        Try Again
      </Button>
    );
  }

  return (
    <Card>
      <CardContent className="p-8">
        <div className="text-center space-y-4">
          <div className="w-16 h-16 mx-auto bg-red-100 rounded-full flex items-center justify-center">
            <svg 
              className="w-8 h-8 text-red-600" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" 
              />
            </svg>
          </div>
          
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {errorTitle}
            </h3>
            <p className="text-gray-600 mb-4">
              {errorDescription}
            </p>
          </div>

          {actionButton}

          {showDetails && (error?.message || error?.data) && (
            <details className="mt-4 text-left">
              <summary className="cursor-pointer text-sm text-gray-500 hover:text-gray-700">
                Technical details
              </summary>
              <pre className="mt-2 p-3 bg-gray-100 rounded text-xs text-gray-700 overflow-auto">
                {error.message}
                {error.status && `\nStatus: ${error.status}`}
                {error.endpoint && `\nEndpoint: ${error.endpoint}`}
                {error.data && `\nResponse: ${JSON.stringify(error.data, null, 2)}`}
              </pre>
            </details>
          )}
        </div>
      </CardContent>
    </Card>
  );
}